import { ApolloError } from "@apollo/client";
import { Box, BoxProps, Flex, Text } from "@chakra-ui/react";
import React, { useMemo } from "react";

import { Alert } from "../../../../components";
import ReadOnlyEmptyNotes from "../../../../components/Images/ReadOnlyEmptyNotes";
import {
  formatRelativeDate,
  getMostRecentDateString,
} from "../../../../utils/datetime";
import { AiInterviewCallFragment, CallNoteFragment } from "../../../graphql";
import { useCopyNotes } from "../../../hooks/useCopyNotes";
import { ClipRange } from "../../../types";
import useAddNote from "../../AiInterviewDetails/useAddNote";
import CallNotesSection from "../../CallNotes/CallNotesSection";
import CopyNotesButtons from "../../CallNotes/CopyNotesButtons";

export type NotesTabProps = BoxProps & {
  call: Pick<
    AiInterviewCallFragment,
    | "id"
    | "duration"
    | "canEdit"
    | "canShare"
    | "canShareExternal"
    | "clipsCount"
    | "position"
    | "speakers"
    | "transcriptLanguageCode"
  >;
  clipId?: string;
  notes: CallNoteFragment[];
  loading: boolean;
  error?: ApolloError;
  onClickTimestamp(t: number): void;
  setClipRange?(c: ClipRange): void;
  playerTime: number;
  notesForCurrentUserOnly: boolean;
};

const NotesTab: React.FC<NotesTabProps> = ({
  call,
  clipId,
  notes,
  loading,
  error,
  onClickTimestamp,
  setClipRange,
  playerTime,
  notesForCurrentUserOnly,
  ...boxProps
}) => {
  const addNote = useAddNote({ callId: call.id });
  const handleCopyNotes = useCopyNotes({ call, generalNotes: notes });

  const notesReadOnly = !call.canEdit;

  const lastUpdatedAt = useMemo(() => {
    if (notes.length === 0) {
      return null;
    }
    return getMostRecentDateString(notes.map((note) => note.updatedAt));
  }, [notes]);

  if (error) {
    return (
      <Alert
        status="error"
        title="Error fetching notes"
        description={error.message}
      />
    );
  }

  if (loading || !call.duration) {
    return null;
  }

  if (notesReadOnly && notes.length === 0) {
    return (
      <Flex alignItems="center" flexDir="column" pt="36">
        <ReadOnlyEmptyNotes />
        <Box fontSize="medium" textAlign="center" pt="6">
          This interview has no notes.
        </Box>
      </Flex>
    );
  }

  return (
    <Box fontSize="sm" pb="3" {...boxProps}>
      <Flex alignItems="center" my="4" gap="2" justifyContent="space-between">
        <Text color="gray.500" fontWeight="400" fontSize="sm">
          {lastUpdatedAt && `Last saved ${formatRelativeDate(lastUpdatedAt)}`}
        </Text>
        <CopyNotesButtons
          onCopy={handleCopyNotes}
          autofillEnabled={false}
          showLabel={false}
        />
      </Flex>
      <Flex direction="column" gap="3">
        {(!notesReadOnly || notes.length > 0) && (
          <Box
            borderRadius="none"
            borderTop="1px"
            borderColor="gray.200"
            overflow="hidden"
            paddingTop={4}
          >
            <CallNotesSection
              callId={call.id}
              clipId={clipId}
              callDuration={call.duration}
              interviewerIds={[]}
              playerTime={playerTime}
              onAddNote={addNote}
              onClickTimestamp={onClickTimestamp}
              notesReadOnly={notesReadOnly}
              setClipRange={setClipRange}
              notes={notes}
              noteProps={{
                px: 0,
              }}
            />
          </Box>
        )}
      </Flex>
    </Box>
  );
};

export default React.memo(NotesTab);
